import {Context, ContextProvider} from "@lit/context";
import {ReactiveControllerHost, ReactiveElement} from "lit";
import {SignalCb} from "@holochain/client";
import {GetStrategy} from "@holochain-open-dev/core-types";

import { Mutex } from 'async-mutex';


// enum InitializationState {
//   Uninitialized = "Uninitialized",
//   InitializingOffline = "InitializingOffline",
//   InitializingOnline = "InitializingOnline",
//   Initialized = "Initialized",
// }


/**
 * ABC of a ViewModel.
 * It mediates the interaction between a View (CustomElements) and a Model (Zome / DNA).
 * It is an Observable meant to be observed by (Lit) ReactiveElements.
 * It is meant to be a singleton passed around by a (Lit) Context.
 * The ViewModel contains a perspective: All the data that a view can observe.
 * To update subscribers, it makes use of Lit's reactive properties.
 * When subscribing, a host must provide a reactive property that has the ViewModel's perspestives's type.
 * Hosts can trigger probing to get an updated perspective.
 * The perspective can be automatically updated by internal events.
 */
 export abstract class ViewModel {

  constructor(public readonly isMainView: boolean) {
    // N/A
  }

  /** -- Fields -- */
  protected _previousPerspective?: unknown;
  protected _providedHosts: [ReactiveControllerHost, PropertyKey][] = [];
  protected _provider?: unknown; // FIXME type: ContextProvider<this.getContext()>;

  protected _canNotify = true; // Master switch for notifying subscribers

  //protected _initializationState: InitializationState = InitializationState.Uninitialized;


  /** -- Abstract fields -- */

  abstract signalHandler?: SignalCb;


  /** -- Abstract methods -- */

  abstract getContext(): Context<unknown, unknown>;
  /* Child class should implement with specific type */
  abstract get perspective(): Object;


  /* Children are expected to override and provide a lightweight and easy-to-compare version of the perspective */
  comparable(): Object { return {} }


  /* (optional) Set startup perspective with local data (source-chain and local DHT cache) */
  async initializePerspectiveFromLocal(): Promise<void> {}
  /* (optional) Set startup perspective with data from the DHT network */
  async initializePerspectiveFromNetwork(): Promise<void> {}
  /* (optional) Called by probeAll() which  */
  protected probeAllInner(_strategy: GetStrategy): void {};

  /**
   * Trigger probing into the network to get the latest up-to-date perspective.
   * Mutex wrapping of probeAllInner as to not do multiple probing at the same time.
   * CAUTION: Don't call probeAll() during a probeAll() or it will deadlock.
   * Should not be async as we expect this to take a long time. Happs are expected to use signals instead to notify changes in data.
   * Probe calls with Network strategy are expected, so a fail-safe try-catch is needed to handle a get() failure.
   */
  protected _probeMutex = new Mutex();
  probeAll(strategy: GetStrategy): void {
    // if (this._initializationState !== InitializationState.Initialized) {
    //   console.warn("probeAll() called on an uninitialized ViewModel");
    //   return;
    // }
    if (this._probeMutex.isLocked()) {
      console.log("probeAll() call skipped. Reason: Already running.");
      return;
    }
    this._probeMutex
      .acquire()
      .then(async (release) => {
          try {
              this.probeAllInner(strategy);
          } catch (e) {
              console.error("probeAll() failed", e);
          }
        release();
      });
  }

  /**
   * Return true if the perspective has changed (compared to _previousPerspective).
   * This will trigger an update on the observers.
   */
  protected hasChanged(): boolean {
    /** return true if previous is empty, meaning comparable() has not been implemented */
    if (!this._previousPerspective || Object.keys(this._previousPerspective).length == 0) {
      return true;
    }
    return Object.entries(this._previousPerspective).toString() != Object.entries(this.comparable()).toString();
  }


  /** -- Final methods -- */

  /** Set ContextProvider for host */
  provideContext(providerHost: ReactiveElement): void {
    console.log(`\t\tProviding context "${this.getContext()}" | in host `, providerHost);
    this._provider = new ContextProvider(providerHost, this.getContext(), this);
  }

  /** -- Observer pattern -- */

  /** */
  subscribe(providedHost: ReactiveControllerHost, propName: PropertyKey): void {
      (providedHost as any)[propName] = this.perspective;
      this._providedHosts.push([providedHost, propName])
  }

  /** */
  unsubscribe(candidate: ReactiveControllerHost): void {
      let index  = 0;
      for (const [host, _propName] of this._providedHosts) {
          if (host === candidate) break;
          index += 1;
      }
      if (index > -1) {
          this._providedHosts.splice(index, 1);
      }
  }

  /** */
  protected notifySubscribers(): boolean {
    if (!this._canNotify || !this.hasChanged()) {
      return false;
    }
    //this._previousPerspective = structuredClone(this.perspective);
    this._previousPerspective = this.comparable();
    for (const [host, propName] of this._providedHosts) {
      if (propName == "") {
        (host as any).requestUpdate();
      } else {
        (host as any)[propName] = this.perspective;
      }
    }
    return true;
  }

}
